import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ActiveOrgService } from '../services/active-org.service';
import { PermissionService } from '../services/permission.service';

/**
 * HTTP interceptor that appends organization_id to org-scoped requests
 * for Superadmin users when an organization is selected.
 *
 * - For write requests (POST, PUT, PATCH, DELETE): appends if selected.
 *   The middleware returns 422 if not present, prompting the UI to ask.
 * - For read requests (GET, HEAD): appends as an optional filter only.
 *
 * Organization-agnostic paths are excluded (no org appended).
 */
@Injectable()
export class ActiveOrgInterceptor implements HttpInterceptor {

  /** Paths that are organization-agnostic (never get org_id). */
  private static readonly ORG_AGNOSTIC_PATHS = [
    '/api/roles',
    '/api/permissions',
    '/api/dropdowns',
    '/api/chat',
    '/api/auth/',
    '/api/login',
    '/api/logout',
  ];

  /**
   * Slice 2: projects are GLOBAL (the backend dropped projects.organization_id),
   * so NO organization_id is ever appended to project requests. This covers the
   * project resource and all its sub-paths that share the /api/projects prefix
   * (list, create, update, delete, /api/projects/dropdown, /api/projects/{id}/pdf).
   *
   * NOTE: this is matched as a path-segment boundary (/api/projects followed by
   * end-of-path, '/', or '?') so it does NOT collide with other paths that merely
   * start with the same characters — in particular /api/organization-project-settings
   * (the retained per-org project-settings endpoint, which resolves the org
   * server-side and MUST keep receiving requests), /api/project-submissions, and
   * /api/project-phases all remain org-scoped as before.
   */
  private static readonly PROJECT_PATH = '/api/projects';

  constructor(
    private activeOrg: ActiveOrgService,
    private permission: PermissionService,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Only applies to Superadmin with a selection.
    if (!this.permission.isSuperadmin() || !this.activeOrg.hasSelection()) {
      return next.handle(request);
    }

    // Skip org-agnostic endpoints.
    if (this.isOrgAgnostic(request.url)) {
      return next.handle(request);
    }

    const orgId = this.activeOrg.activeOrgId();

    // Append to query params for GET/HEAD; to body for write verbs.
    if (['GET', 'HEAD', 'OPTIONS'].includes(request.method.toUpperCase())) {
      const updated = request.clone({
        params: request.params.set('organization_id', String(orgId)),
      });
      return next.handle(updated);
    }

    // Write verbs: add to body if body is an object (JSON).
    if (request.body && typeof request.body === 'object' && !(request.body instanceof FormData)) {
      const updatedBody = { ...request.body, organization_id: orgId };
      const updated = request.clone({ body: updatedBody });
      return next.handle(updated);
    }

    // FormData or other: add as query param.
    const updated = request.clone({
      params: request.params.set('organization_id', String(orgId)),
    });
    return next.handle(updated);
  }

  private isOrgAgnostic(url: string): boolean {
    if (ActiveOrgInterceptor.ORG_AGNOSTIC_PATHS.some(path => url.includes(path))) {
      return true;
    }
    return this.isProjectPath(url);
  }

  /**
   * True when the request targets the global /api/projects resource (or one of
   * its sub-paths), matched at a path-segment boundary so it never matches
   * /api/organization-project-settings, /api/project-submissions, or similar.
   */
  private isProjectPath(url: string): boolean {
    const prefix = ActiveOrgInterceptor.PROJECT_PATH;
    const idx = url.indexOf(prefix);
    if (idx === -1) {
      return false;
    }
    const nextChar = url.charAt(idx + prefix.length);
    // Exact resource, a sub-path (/...), or the start of a query string (?...).
    return nextChar === '' || nextChar === '/' || nextChar === '?';
  }
}
